/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AppError } from "@tivocorp/data-domain-lib";

import { ErrorTypes } from "../../model/error";
import { BaseErrorMapper } from "../../model/errorMapper/BaseErrorMapper";
import { Locale, LocaleKeys } from "../../model/locale";

/**
 * Catalog code for the Content Detail error dialog. Shown when the combined
 * screen-details (ActionsService) + Preview load fails.
 *
 * Taken from `ErrorTypes` rather than written out here, so the number has one
 * declaration.
 */
export const CONTENT_DETAIL_ERROR_CODE: string =
  ErrorTypes.CONTENT_DETAIL_MAIN_ERROR_A716;

/** Diagnostic message logged/reported when the load has no actions to show. */
const CONTENT_DETAIL_NO_ACTIONS_MESSAGE: string =
  "Content detail returned no actions";

/**
 * Localised title/body for A716 — shared by the screen-level dialog and the
 * May Also Like sheet's toast, so both surfaces report the same failure the
 * same way.
 *
 * Read on each call rather than captured at module load: the locale files
 * load asynchronously, so a value resolved at import time would be empty.
 */
export const getContentDetailErrorTitle = (): string =>
  Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE);

/** Localised body for A716. See {@link getContentDetailErrorTitle}. */
export const getContentDetailErrorDescription = (): string =>
  Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION);

/**
 * Error mapper for the Content Detail screen (A716).
 *
 * A716 is triggered when the screen-details (ActionsService) call or the
 * Preview call fails. A successful load always renders the screen, even when
 * it resolves to no watch/schedule actions — see {@link fromNoActions} for
 * the (now unused by the load thunk) no-actions variant kept for callers that
 * still want to report that condition explicitly.
 *
 * The sub-error code comes from {@link ErrorCodeUtil}, shared by every
 * screen, so the same failure always reports the same sub-code:
 * - N6xx: HTTP client / transport — no HTTP response received
 * - S6xx: service context — token, service location, response integrity
 * - G4xx-G6xx: HTTP status returned by ActionsService/Preview
 * - none: nothing displayable, or a failure no layer recognises
 *
 * A sub-code is never guessed: a near-enough code is worse than none because
 * it looks precise.
 */
export class ContentDetailErrorMapper extends BaseErrorMapper {
  /**
   * Maps a raw error thrown during the Content Detail load to an A716
   * AppError with the appropriate sub-error code.
   *
   * @param error - The raw error thrown by the HTTP client or service layer
   * @returns AppError with code "A716" and a sub-error suffix, or with no
   *          suffix when no layer recognised the failure
   */
  public static fromError(error: unknown): AppError {
    return this.buildAppError(
      CONTENT_DETAIL_ERROR_CODE,
      this.extractMessage(error),
      this.extractSubErrorCode(error),
      getContentDetailErrorTitle(),
      getContentDetailErrorDescription()
    );
  }

  /**
   * Creates a bare A716 AppError for a load that succeeded at every layer but
   * resolved to nothing the screen can act on (no watch/schedule actions).
   *
   * No sub-error code is appended — the failure is not at the network,
   * service context, or HTTP layer.
   *
   * @returns AppError with code "A716" and no sub-error code
   */
  public static fromNoActions(): AppError {
    return this.buildAppError(
      CONTENT_DETAIL_ERROR_CODE,
      CONTENT_DETAIL_NO_ACTIONS_MESSAGE,
      undefined,
      getContentDetailErrorTitle(),
      getContentDetailErrorDescription()
    );
  }

  /**
   * Composite code for the error dialog: `"A716-G500"`, or plain `"A716"`
   * when there is no sub-error code to show.
   *
   * @param error - The raw error thrown by the HTTP client or service layer
   * @returns The composite code to display and report
   */
  public static toCompositeCode(error: unknown): string {
    return this.fromError(error).getUIErrorwithSubErrorCode();
  }
}

/**
 * Message for the May Also Like sheet's A716 toast: the same description
 * {@link getContentDetailErrorDescription} gives the dialog, plus the
 * composite code in parentheses (e.g. `"We are currently experiencing a
 * temporary service connection issue. Please try again later. (A716-G500)"`)
 * — the same "(code)" suffix convention the sign-in terminal alert uses.
 *
 * @param error - The raw error thrown by the HTTP client or service layer
 * @returns The toast's message text
 */
export function getContentDetailSheetErrorMessage(error: unknown): string {
  const code: string = ContentDetailErrorMapper.toCompositeCode(error);
  return getContentDetailErrorDescription() + " (" + code + ")";
}
